import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShoppingCart, Plus, Minus, X } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { PageHero, Empty } from "../components/ui-parts";
import { ODISHA_DISTRICTS } from "../lib/districts";

export default function Books() {
  const [books, setBooks] = useState([]);
  const [cart, setCart] = useState([]);
  const [showCheckout, setShowCheckout] = useState(false);
  const [form, setForm] = useState({ full_name: "", mobile: "", email: "", address: "", district: "" });
  const [done, setDone] = useState(null);

  useEffect(() => { api.get("/books").then(r => setBooks(r.data)); }, []);

  const add = (b) => {
    setCart(prev => {
      const ex = prev.find(x => x.id === b.id);
      if (ex) return prev.map(x => x.id === b.id ? { ...x, qty: x.qty + 1 } : x);
      return [...prev, { id: b.id, title: b.title, price: b.price || 0, qty: 1 }];
    });
    toast.success(`Added: ${b.title}`);
  };
  const inc = (id) => setCart(c => c.map(x => x.id === id ? { ...x, qty: x.qty + 1 } : x));
  const dec = (id) => setCart(c => c.map(x => x.id === id ? { ...x, qty: Math.max(1, x.qty - 1) } : x));
  const rm = (id) => setCart(c => c.filter(x => x.id !== id));
  const total = cart.reduce((a, b) => a + b.price * b.qty, 0);

  const checkout = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/book-orders", { ...form, items: cart });
      setDone(data.ref); setCart([]); toast.success(`Order placed: ${data.ref}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Order failed"); }
  };

  return (
    <div data-testid="books-page">
      <PageHero badge="Publications" title="Sanskrit Book Store" subtitle="Books, teaching materials and publications from Jayatu Sanskrutam." />
      <section className="section">
        <div className="container-narrow">
          {books.length === 0 ? <Empty title="Book catalogue coming soon" hint="Admin will add real books and prices here shortly." /> :
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {books.map(b => (
                <div key={b.id} className="js-card p-4" data-testid={`book-${b.id}`}>
                  {b.cover_url ? <img src={fileUrl(b.cover_url)} alt="" className="w-full h-52 object-cover rounded-md mb-3" /> : <div className="w-full h-52 bg-[var(--js-bg)] rounded-md mb-3 flex items-center justify-center text-[var(--js-muted)]">No cover</div>}
                  <div className="font-heading text-lg text-[var(--js-primary)] line-clamp-2">{b.title}</div>
                  <div className="text-xs text-[var(--js-muted)] mt-1">{b.author}</div>
                  <p className="text-xs text-[var(--js-muted)] mt-2 line-clamp-2">{b.description}</p>
                  <div className="flex items-center justify-between mt-3">
                    <div className="font-semibold text-[var(--js-primary)]">{b.price ? `₹ ${b.price}` : "—"}</div>
                    <button onClick={() => add(b)} data-testid={`book-add-${b.id}`} className="js-btn-primary !py-1.5 !px-3 !text-xs"><Plus size={12} /> Cart</button>
                  </div>
                </div>
              ))}
            </div>
          }
        </div>
      </section>

      {cart.length > 0 && (
        <div className="fixed bottom-4 right-4 z-40 js-card p-4 w-80 shadow-xl" data-testid="cart-widget">
          <div className="flex items-center justify-between mb-3"><div className="font-heading text-lg flex items-center gap-2"><ShoppingCart size={16} /> Cart</div><button onClick={() => setCart([])}><X size={16} /></button></div>
          <div className="space-y-2 max-h-48 overflow-auto">
            {cart.map(i => (
              <div key={i.id} className="flex items-center justify-between text-sm">
                <div className="flex-1 truncate mr-2">{i.title}</div>
                <div className="flex items-center gap-1"><button onClick={() => dec(i.id)}><Minus size={12} /></button><span className="w-6 text-center">{i.qty}</span><button onClick={() => inc(i.id)}><Plus size={12} /></button><button onClick={() => rm(i.id)} className="ml-2 text-[var(--js-primary)]"><X size={12} /></button></div>
              </div>
            ))}
          </div>
          <div className="border-t border-[var(--js-border)] mt-3 pt-3 flex justify-between text-sm font-semibold"><span>Total</span><span>₹ {total}</span></div>
          <button onClick={() => setShowCheckout(true)} className="js-btn-primary w-full justify-center mt-3" data-testid="cart-checkout">Checkout</button>
        </div>
      )}

      {showCheckout && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowCheckout(false)}>
          <div className="js-card p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="checkout-modal">
            <div className="flex justify-between items-center mb-4"><h3 className="font-heading text-xl text-[var(--js-primary)]">Checkout</h3><button onClick={() => setShowCheckout(false)}><X size={18} /></button></div>
            {done ? <div className="py-6 text-center"><p className="font-semibold">Order placed!</p><p className="font-mono text-[var(--js-primary)] mt-2">{done}</p><p className="text-sm text-[var(--js-muted)] mt-2">Our team will contact you for delivery and payment.</p></div> :
              <form onSubmit={checkout} className="space-y-3">
                {[["full_name", "Full Name"], ["mobile", "Mobile"], ["email", "Email"], ["address", "Delivery Address"]].map(([k, l]) => (
                  <input key={k} required type={k === "email" ? "email" : "text"} placeholder={l} value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} className="w-full px-3 py-2 rounded-md border border-[var(--js-border)] text-sm" data-testid={`checkout-${k}`} />
                ))}
                <select required value={form.district} onChange={e => setForm(f => ({ ...f, district: e.target.value }))} className="w-full px-3 py-2 rounded-md border border-[var(--js-border)] text-sm" data-testid="checkout-district">
                  <option value="">Select District</option>{ODISHA_DISTRICTS.map(d => <option key={d}>{d}</option>)}
                </select>
                <div className="text-sm font-semibold">Total: ₹ {total}</div>
                <button className="js-btn-primary w-full justify-center" data-testid="checkout-submit">Place Order</button>
              </form>
            }
          </div>
        </div>
      )}
    </div>
  );
}
